import { create } from 'zustand';
import { fetchPreferences, updatePreferences } from './meApi';

export type ThemeOption = 'light' | 'dark' | 'system';

const LS_KEY = 'jurivoxa-theme';

/**
 * Theme belongs to the PERSON, not the browser (docs/SETTINGS-MODEL.md §4.4).
 *
 * It is stored on the account (`UserPreference.theme`) so it follows the barrister to a second
 * machine and survives clearing site data. `localStorage` stays as a PAINT CACHE only: the page
 * must colour itself before any network call returns, otherwise every reload flashes white
 * before going dark. The cache is refreshed from the server the moment the session is known,
 * and the server always wins.
 */
function resolveAndApply(theme: ThemeOption): void {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark);
  document.documentElement.classList.toggle('dark', isDark);
}

interface ThemeState {
  theme: ThemeOption;
  /** True once the account's stored theme has been read; false while the paint cache is in use. */
  hydrated: boolean;
  setTheme: (t: ThemeOption) => void;
}

export const useThemeStore = create<ThemeState>()((set, get) => ({
  theme: (localStorage.getItem(LS_KEY) as ThemeOption | null) ?? 'light',
  hydrated: false,

  setTheme(theme) {
    if (get().theme === theme) return;
    // Paint immediately, then persist. A colour change must never wait on the network.
    localStorage.setItem(LS_KEY, theme);
    set({ theme });
    resolveAndApply(theme);
    void updatePreferences({ theme }).catch(() => {
      // The choice still applies on this device; the next successful save or hydration
      // reconciles it. Never surface a toast for a preference write.
    });
  },
}));

/**
 * Call once at app startup (main.tsx).
 * Re-applies the cached theme and wires the OS change listener so System mode reacts in real
 * time when the OS switches at sunset/sunrise.
 */
export function initThemeListener(): void {
  resolveAndApply(useThemeStore.getState().theme);

  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      if (useThemeStore.getState().theme === 'system') {
        resolveAndApply('system');
      }
    });
}

/**
 * Load the account's stored theme and apply it. Call once the user is authenticated — before
 * that there is no account to read from. Safe to call more than once.
 */
export async function hydrateThemeFromAccount(): Promise<void> {
  if (useThemeStore.getState().hydrated) return;
  try {
    const prefs = await fetchPreferences();

    // ADOPT, don't overwrite. A user who chose dark mode before preferences were stored on the
    // account has that choice in localStorage and nothing on the server. Taking the server's
    // 'light' default here would flip them to light at their next sign-in — so when the account
    // has never saved a preference, the browser's existing choice wins and is pushed up once.
    const cached = localStorage.getItem(LS_KEY) as ThemeOption | null;
    if (!prefs.initialised && cached && cached !== prefs.theme) {
      useThemeStore.setState({ theme: cached, hydrated: true });
      resolveAndApply(cached);
      void updatePreferences({ theme: cached }).catch(() => {});
      return;
    }

    useThemeStore.setState({ theme: prefs.theme, hydrated: true });
    localStorage.setItem(LS_KEY, prefs.theme);
    resolveAndApply(prefs.theme);
  } catch {
    // Offline, or the session is not ready. The paint cache remains in force and the next
    // sign-in hydrates again — a preference must never block the app from rendering.
  }
}
